import React, { useRef, useState, useEffect, useCallback } from 'react';
import config from '../config';
import { lerp, clamp, ease, damp, isMobile, isReduced } from '../hooks/useAnimation';
import '../styles/Hero.css';

// 六张 AI 接待卡牌数据
const CARDS = [
  {
    num: '01',
    tag: 'CLIENT',
    title: '接待客户',
    promise: '全天候，接住每一次询单。',
    detail: '它替你展示最好的产品，听懂需求，接住询单——你睡觉的时候，它也在营业。',
    visual: 'sv-client'
  },
  {
    num: '02',
    tag: 'TEAM',
    title: '接待同事',
    promise: '背景一次讲清，拿来就能继续做。',
    detail: '你产出的信息全貌，它替你讲清楚。同事拿来就能二次加工，不用再追着你要背景。',
    visual: 'sv-team'
  },
  {
    num: '03',
    tag: 'LEADERSHIP',
    title: '接待领导',
    promise: '报告聚焦，追问始终有答案。',
    detail: '一份聚焦的报告，一个全量的 ZIP，它替你持续回答追问。无惧领导追问，坦荡信息穿透。',
    visual: 'sv-report'
  },
  {
    num: '04',
    tag: 'PARTNER',
    title: '接待伙伴',
    promise: '创意，在连接里长出来。',
    detail: '伙伴直接和你的 APP 协同，而不是在会议里磨出来。',
    visual: 'sv-partner'
  },
  {
    num: '05',
    tag: 'DATA',
    title: '接通数据',
    promise: '一次接通，全员随时可用。',
    detail: '数据只接通一次，所有人都能随时调用。',
    visual: 'sv-data'
  },
  {
    num: '06',
    tag: 'FUTURE',
    title: '连接未来',
    promise: '今天创造，定义明天。',
    detail: '今天做 AI APP 的人，定义明天的工作方式。',
    visual: 'sv-future'
  },
];

const NC = CARDS.length;

// 动画常量
const HERO_ENTER_DELAY = 90;
const HERO_ENTER_STEP = 55;
const HERO_ENTER_DURATION = 620;

// 场景视觉组件
function SceneVisual({ type }) {
  switch (type) {
    case 'sv-client':
      return (
        <div className="scene-visual sv-client">
          <span className="sv-status"><i></i>24H 在线接待</span>
          <span className="sv-message sv-message--in"><small>客户</small><b>想了解企业版</b></span>
          <span className="sv-message sv-message--out"><small>NUWORK</small><b>已生成专属方案</b><em>↗</em></span>
        </div>
      );
    case 'sv-team':
      return (
        <div className="scene-visual sv-team">
          <span className="sv-team-card sv-team-card--source"><small>SOURCE</small><b>完整背景</b><i></i><i></i></span>
          <span className="sv-team-transfer"></span>
          <span className="sv-team-card sv-team-card--ready"><small>READY</small><b>继续加工</b><i></i><i></i></span>
        </div>
      );
    case 'sv-report':
      return (
        <div className="scene-visual sv-report">
          <span className="sv-report-sheet"><small>REPORT / 08</small><b>关键结论</b><span className="sv-report-chart"><i></i><i></i><i></i><i></i></span><em>全量依据已关联</em></span>
          <span className="sv-query sv-query--a">为什么？</span>
          <span className="sv-query sv-query--b">下一步？</span>
        </div>
      );
    case 'sv-partner':
      return (
        <div className="scene-visual sv-partner">
          <span className="sv-partner-app sv-partner-app--mine"><i className="sv-app-mark">Nu</i><b>你的 APP</b><small>ONLINE</small></span>
          <span className="sv-partner-link"></span>
          <span className="sv-partner-app sv-partner-app--peer"><i className="sv-app-mark">P</i><b>伙伴 APP</b><small>CONNECTED</small></span>
        </div>
      );
    case 'sv-data':
      return (
        <div className="scene-visual sv-data">
          <span className="sv-data-sources"><i>CRM</i><i>DOC</i><i>ERP</i></span>
          <span className="sv-data-rail"><i></i></span>
          <span className="sv-data-core"><small>SYNC</small><b>一次接通</b></span>
          <span className="sv-data-ready"><i></i>全员可用</span>
        </div>
      );
    case 'sv-future':
      return (
        <div className="scene-visual sv-future">
          <span className="sv-future-kicker">AI APP · TODAY</span>
          <span className="sv-future-path"><i></i><i></i><i></i></span>
          <span className="sv-future-node sv-future-node--now"><small>NOW</small><b>今天创造</b></span>
          <span className="sv-future-node sv-future-node--next"><small>NEXT</small><b>定义工作方式</b></span>
        </div>
      );
    default:
      return null;
  }
}

function Hero() {
  const heroRef = useRef(null);
  const deckRef = useRef(null);
  const cardsRef = useRef([]);
  const [deckIndex, setDeckIndex] = useState(0);
  
  // 动画状态
  const [hoverCard, setHoverCard] = useState(-1);
  const cardLiftRef = useRef(CARDS.map(() => 0));
  
  // 动画参数
  const animRef = useRef({
    smY: 0,
    mx: 0,
    mxT: 0,
    frameRunning: false,
    lastFrameTime: performance.now(),
    T0: performance.now(),
    introEnabled: true,
    M: { hero: { top: 0, h: 0 }, deckW: 0, cardW: 294 },
  });

  // 测量尺寸
  const measure = useCallback(() => {
    const heroEl = heroRef.current;
    const deckEl = deckRef.current;
    if (!heroEl || !deckEl) return;

    const rect = heroEl.getBoundingClientRect();
    animRef.current.M = {
      hero: { top: rect.top + window.scrollY, h: heroEl.offsetHeight },
      deckW: deckEl.clientWidth || window.innerWidth,
      cardW: cardsRef.current[0]?.offsetWidth || 294,
    };
  }, []);

  // 动画帧
  const frame = useCallback((now) => {
    const anim = animRef.current;
    const { M } = anim;
    const vh = window.innerHeight;
    const y = window.scrollY;
    const motion = !isMobile() && !isReduced();
    const heroMotion = motion && window.innerWidth > 1180;

    const dt = Math.min(0.05, Math.max(0.001, (now - anim.lastFrameTime) / 1000));
    anim.lastFrameTime = now;

    const heroTop = M.hero.top - y;
    const heroActive = heroTop < vh && heroTop + M.hero.h > 0;

    // 平滑滚动值
    if (heroActive) {
      anim.smY = damp(anim.smY, y, 5.3, dt);
      if (Math.abs(y - anim.smY) < 0.3) anim.smY = y;
    } else {
      anim.smY = y;
    }

    // 平滑鼠标值
    if (heroActive) {
      anim.mx = damp(anim.mx, anim.mxT, 5.7, dt);
      if (Math.abs(anim.mxT - anim.mx) < 0.001) anim.mx = anim.mxT;
    } else {
      anim.mx = anim.mxT;
    }

    let needsFrame = heroActive && Math.abs(y - anim.smY) >= 0.3;

    // 卡片动画
    if (!heroMotion) {
      // 移动端：无动画
      cardsRef.current.forEach((c) => {
        if (!c) return;
        c.style.transform = '';
        c.style.opacity = 1;
        c.style.pointerEvents = '';
        c.style.zIndex = '';
        c.classList.remove('is-drawn');
      });
    } else if (heroMotion && heroActive) {
      const deckW = M.deckW || window.innerWidth;
      const cardW = M.cardW || 294;
      const overviewScale = clamp((deckW - 70) / Math.max(NC * cardW + Math.max(0, NC - 1) * 14, 1), 0.52, 0.84);
      const overviewGap = cardW * overviewScale + 14;
      const scrollP = clamp((anim.smY - M.hero.top) / Math.max(M.hero.h - vh, 1), 0, 1);
      
      // 收纳进度
      const dockP = ease(clamp((scrollP - 0.045) / 0.78, 0, 1));
      const dockAnchor = Math.max(150, deckW * 0.5 - cardW * 0.64);

      let cardPending = false;

      cardsRef.current.forEach((c, i) => {
        if (!c) return;
        const k = i - (NC - 1) / 2;
        
        // 入场动画
        const ip = anim.introEnabled 
          ? ease(clamp((now - anim.T0 - HERO_ENTER_DELAY - i * HERO_ENTER_STEP) / HERO_ENTER_DURATION, 0, 1)) 
          : 1;
        const enterY = lerp(32, 0, ip);

        // 位置计算
        const overviewX = k * overviewGap;
        const dockLayer = i - (NC - 1);
        const dockX = dockAnchor + dockLayer * 6.5;
        const x = lerp(overviewX, dockX, dockP) + anim.mx * (4 + dockP * Math.abs(k) * 1.5);

        // 悬停抬起
        const liftTarget = hoverCard === i ? -11 : 0;
        cardLiftRef.current[i] = damp(cardLiftRef.current[i], liftTarget, 13.4, dt);
        if (Math.abs(liftTarget - cardLiftRef.current[i]) > 0.08) cardPending = true;

        const drawn = clamp(-cardLiftRef.current[i] / 11, 0, 1);

        // Y 位置
        const overviewY = Math.abs(k) * 1.4;
        const dockY = dockLayer * 1.6;
        const yy = lerp(overviewY, dockY, dockP) + enterY + cardLiftRef.current[i];

        // 旋转
        const dockRotation = dockLayer * 0.42;
        const rotation = lerp(0, dockRotation, dockP) + anim.mx * 0.32;

        // 缩放
        const baseScale = lerp(overviewScale, 0.91, dockP);

        // 应用样式
        c.style.opacity = ip;
        c.style.pointerEvents = dockP < 0.72 ? 'auto' : (i === NC - 1 ? 'auto' : 'none');
        c.style.zIndex = hoverCard === i ? 40 : (dockP < 0.62 ? 20 + i : 20 + i);
        c.classList.toggle('is-drawn', drawn > 0.08);
        c.style.transform = `translate(${x}px, ${yy}px) rotate(${rotation}deg) scale(${baseScale + drawn * 0.018})`;
      });

      const introPending = anim.introEnabled && now < anim.T0 + HERO_ENTER_DELAY + Math.max(0, NC - 1) * HERO_ENTER_STEP + HERO_ENTER_DURATION;
      needsFrame = needsFrame || introPending || cardPending || Math.abs(anim.mxT - anim.mx) > 0.001;
    }

    if (motion && needsFrame && !document.hidden) {
      requestAnimationFrame(frame);
    } else {
      anim.frameRunning = false;
    }
  }, [hoverCard]);

  // 启动动画帧
  const startFrame = useCallback(() => {
    const anim = animRef.current;
    if (anim.frameRunning) return;
    anim.frameRunning = true;
    anim.lastFrameTime = performance.now();
    requestAnimationFrame(frame);
  }, [frame]);

  // 鼠标移动
  const handlePointerMove = useCallback((e) => {
    if (isMobile() || isReduced()) return;
    animRef.current.mxT = e.clientX / window.innerWidth - 0.5;
    startFrame();
  }, [startFrame]);

  const handlePointerLeave = useCallback(() => {
    animRef.current.mxT = 0;
    startFrame();
  }, [startFrame]);

  // 卡片悬停
  const handleCardEnter = useCallback((index) => {
    if (isMobile() || isReduced()) return;
    setHoverCard(index);
    startFrame();
  }, [startFrame]);

  const handleCardLeave = useCallback(() => {
    setHoverCard(-1);
    startFrame();
  }, [startFrame]);

  // 移动端轨道控制
  const handlePrev = useCallback(() => {
    const newIndex = Math.max(0, deckIndex - 1);
    setDeckIndex(newIndex);
    const deck = deckRef.current;
    const card = cardsRef.current[newIndex];
    if (deck && card) {
      const left = card.offsetLeft - (deck.clientWidth - card.offsetWidth) / 2;
      deck.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
    }
  }, [deckIndex]);

  const handleNext = useCallback(() => {
    const newIndex = Math.min(NC - 1, deckIndex + 1);
    setDeckIndex(newIndex);
    const deck = deckRef.current;
    const card = cardsRef.current[newIndex];
    if (deck && card) {
      const left = card.offsetLeft - (deck.clientWidth - card.offsetWidth) / 2;
      deck.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
    }
  }, [deckIndex]);

  // 初始化
  useEffect(() => {
    if (window.scrollY >= 24) {
      animRef.current.introEnabled = false;
    }
    animRef.current.smY = window.scrollY;
    measure();
    startFrame();

    const handleScroll = () => startFrame();
    const handleResize = () => {
      animRef.current.smY = window.scrollY;
      measure();
      startFrame();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);
    window.addEventListener('load', () => {
      if (window.scrollY >= 24) animRef.current.introEnabled = false;
      measure();
      startFrame();
    });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [measure, startFrame]);

  return (
    <section 
      className="hero" 
      id="heroSec" 
      ref={heroRef}
      onPointerMove={handlePointerMove}
      onPointerLeave={handlePointerLeave}
    >
      <div className="hero-sticky">
        <div className="hero-eyebrow">NUWORK.CN · 非凡工作</div>
        <h1>
          <span className="hero-title-shine">
            人人做 <b>APP</b>，<br/><em>APP 来接客</em>
          </span>
        </h1>
        <p className="hero-question">帮你接待的，何必再是微信？</p>
        <p className="hero-copy">
          <span>在非凡工作里，人人都能用 <strong>workbuddy</strong> 写自己的 AI 应用——替你展示自我、衔接需求、拉通工作、协同伙伴。</span>
        </p>
        
        <div className="hero-cta">
          <a className="pill pill-dark pill-lg" href={config.market.home} target="_blank" rel="noopener noreferrer">
            探索应用市场 <span aria-hidden="true">↗</span>
          </a>
          <a className="hero-link" href="#deck">
            看看它能替我接待谁 <span aria-hidden="true">↓</span>
          </a>
        </div>

        <div 
          className="deck" 
          id="deck" 
          ref={deckRef}
          role="list"
          aria-label="六个高效接待场景"
          onPointerLeave={handleCardLeave}
        >
          {CARDS.map((card, i) => (
            <article
              key={i}
              className="tarot"
              role="listitem"
              ref={(el) => (cardsRef.current[i] = el)}
              onPointerEnter={() => handleCardEnter(i)}
            >
              <div className="scene-meta">
                <b>{card.num}</b>
                <span>{card.tag}</span>
              </div>
              <div className="scene-art" aria-hidden="true">
                <SceneVisual type={card.visual} />
              </div>
              <div className="scene-copy">
                <h2>{card.title}</h2>
                <p className="scene-promise">{card.promise}</p>
                <p className="scene-detail">{card.detail}</p>
              </div>
            </article>
          ))}
        </div>

        <div className="deck-mobile-controls" aria-label="接待场景切换">
          <span>
            <output id="deckRailStatus" aria-live="polite">{deckIndex + 1} / {NC}</output>
            左右滑动查看
          </span>
          <div>
            <button 
              id="deckPrev" 
              type="button" 
              aria-label="上一个接待场景" 
              disabled={deckIndex <= 0}
              onClick={handlePrev}
            >←</button>
            <button 
              id="deckNext" 
              type="button" 
              aria-label="下一个接待场景"
              disabled={deckIndex >= NC - 1}
              onClick={handleNext}
            >→</button>
          </div>
        </div>

        <p className="hero-epilogue">发现前所未有的有趣灵魂，接待每一个特殊的他。</p>
      </div>
    </section>
  );
}

export default Hero;
